import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Permission } from './data/permission';
import { AuthenticationUser, AuthService, PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { PermissionTypeStringToEnumUtil } from '../util/PermissionTypeStringToEnumUtil';
import { TranslocoService } from '@jsverse/transloco';
import { SessionService } from './session.service';

@Injectable({
	providedIn: 'root',
})
export class PermissionsService {
	private readonly httpClient = inject(HttpClient);
	private readonly authService = inject(AuthService);
	private readonly translocoService = inject(TranslocoService);
	private readonly sessionService = inject(SessionService);

	private permissionsJson = 'assets/mocks.json';

	getPermissionsOwner(): Observable<any> {
		return this.httpClient.get(this.permissionsJson);
	}

	createPermission(permission: Permission, sendMail: boolean = false) {
		const authUserTO: AuthenticationUser = this.createAuthUser(permission);
		return this.authService.generateAuthCodeForRealEstate(this.sessionService.effectiveArea, sendMail, authUserTO);
	}

	createAuthUser(permission: Permission): AuthenticationUser {
		const permissionObject: PermissionObject = this.permissionToPermissionObject(permission);
		const permissions = [];
		permissions.push(permissionObject);
		const authUserTO: AuthenticationUser = {
			keycloakUserId: '',
			firstName: permission.firstName,
			lastName: permission.lastName,
			permissions,
		};
		return authUserTO;
	}

	getPermissionByAuthCode(authCode: string) {
		return this.authService.getPermissionByAuthCode(this.sessionService.effectiveArea, authCode);
	}

	permitByCode(firstName: string, lastName: string, code: string, keycloakUserId: string) {
		const permission: PermissionObject = {
			area: this.sessionService.effectiveArea,
			authCode: code,
		};
		const authUserTO: AuthenticationUser = {
			keycloakUserId,
			firstName,
			lastName,
			permissions: [permission],
		};
		return this.authService.permitUserToBillingUnitByCode(this.sessionService.effectiveArea, authUserTO);
	}

	permitGroupByCode(authCode: string) {
		return this.authService.permitGroupByAuthCode(this.sessionService.effectiveArea, authCode);
	}

	public getPermissionsFromUserAuthTO(authUserTO: AuthenticationUser): Permission[] {
		const permissions = [];
		if (authUserTO) {
			for (const permissionObject of authUserTO.permissions) {
				const permission = new Permission(
					authUserTO.keycloakUserId,
					authUserTO.firstName,
					authUserTO.lastName,
					permissionObject.authCode,
					permissionObject.createDate,
					permissionObject.permissionType,
					permissionObject.permissionUserId,
					permissionObject.permissionCreatorId,
					permissionObject.tenantUnitId,
					permissionObject.area,
					permissionObject.email,
					permissionObject.comment,
					permissionObject.codeActivatedOn,
					permissionObject?.codeValidUntil
				);
				permissions.push(permission);
			}
		}
		return permissions;
	}

	public getAllPermissionsForPermissionUser(permissionsUserId: string) {
		return this.authService.getPermissionsByPermissionUserId(this.sessionService.effectiveArea, permissionsUserId);
	}

	public getAuthUserByPermissionAuthCode(authCode: string) {
		return this.authService.getAuthenticationUserByAuthCode(this.sessionService.effectiveArea, authCode);
	}

	public getPermissionsForUser(area: number, userId: string) {
		return this.authService.getAllActivePermissionsForUser(area, userId);
	}

	public getGroupPermissionsForUser(area: number, userId: string) {
		return this.authService.getAllAuthenticationUsersInGroupByUser(area, userId);
	}

	public deleteGroupPermissionByAuthCode(area: number, authCode: string) {
		return this.authService.deleteGroupPermissionByAuthCode(area, authCode);
	}

	getPermissionsString(permissions: Permission[], strLen: number): string {
		permissions = permissions.sort((a, b) => {
			return (new Date(b.codeValidUntil) as any) - (new Date(a.codeValidUntil) as any);
		});
		let permissionsString = this.translocoService.translate('permissions.noPermissionsFound');
		if (permissions.length > 0) {
			permissionsString = '';
			for (const permission of permissions) {
				if (permission.lastName || permission.firstName) {
					if (permissionsString === '') {
						permissionsString += permission.firstName + ' ' + permission.lastName;
					} else {
						permissionsString += ', ' + permission.firstName + ' ' + permission.lastName;
					}
				} else if (permission.authCode) {
					if (permissionsString === '') {
						permissionsString += permission.authCode;
					} else {
						permissionsString += ', ' + permission.authCode;
					}
				}
				permissionsString = this.formatPermissionsString(permissionsString, strLen);
				if (permissionsString.length >= strLen) {
					break;
				}
			}
		}
		return permissionsString;
	}

	formatPermissionsString(str: string, strLen: number): string {
		if (str.length > strLen) {
			str = str.substr(0, strLen).trim();
			if (str.endsWith(',')) {
				str = str.substr(0, str.length - 1);
			}
			str += '...';
		}
		return str;
	}

	public permissionTypeToReadable(permissionType: string) {
		switch (permissionType) {
			case 'OWNER':
				return this.translocoService.translate('permissions.owner');
			case 'RESIDENT':
				return this.translocoService.translate('permissions.resident');
			case 'PROPERTY_MANAGEMENT':
				return this.translocoService.translate('permissions.propertyManagement');
		}
	}

	permissionToPermissionObject(permission: Permission): PermissionObject {
		return {
			permissionUserId: permission.permissionUserId,
			permissionCreatorId: permission.permissionCreatorId,
			authCode: permission.authCode,
			tenantUnitId: permission.tenantUnitId,
			permissionType: PermissionTypeStringToEnumUtil.getPermissionTypeByName(permission.permissionType),
			comment: permission.comment,
			area: permission.area,
			email: permission.email,
		};
	}

	permissionObjectToPermission(permissionObj: PermissionObject): Permission {
		return new Permission(
			'',
			permissionObj.firstName,
			permissionObj.lastName,
			permissionObj.authCode,
			permissionObj.createDate,
			permissionObj.permissionType,
			permissionObj.permissionUserId,
			permissionObj.permissionCreatorId,
			permissionObj.tenantUnitId,
			permissionObj.area,
			permissionObj.email,
			permissionObj.comment,
			permissionObj.codeActivatedOn,
			permissionObj.codeValidUntil
		);
	}
}
